import * as React from "react";
import {Alert, Button, Modal} from "react-bootstrap/lib";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AccessRule, SpatialModel} from "../model";
import {SecurityForm} from "../security/SecurityForm";
import {Role} from "../user/model";
import {RESTRICT_NAVIGATION_BOUNDS} from "../common/ui/riamap/Previewer";
import {RIAMap} from "../common/ui/riamap/RIAMap";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {Api} from "../api/InternalApi";
import {Col, ControlLabel, FormControl, FormGroup, InputGroup, OverlayTrigger, Tooltip} from "react-bootstrap";
import {Form} from "react-bootstrap/lib";
import {UrlStore} from "@luciad/ria/model/store/UrlStore.js";
import {GeoJsonCodec} from "@luciad/ria/model/codec/GeoJsonCodec.js";
import {FeatureModel} from "@luciad/ria/model/feature/FeatureModel.js";
import {FeatureLayer} from "@luciad/ria/view/feature/FeatureLayer.js";
import {GMLCodec} from "@luciad/ria/model/codec/GMLCodec.js";
import {addReference, isValidReferenceIdentifier, parseWellKnownText} from "@luciad/ria/reference/ReferenceProvider.js";
import * as epsgIndexAll from "epsg-index/all.json";
import {validEPSGCode} from "../security/model";

const allEpsg = epsgIndexAll;

export interface DataSecurityDialogProps {
  onSuccess?: () => void;
  onHide?: () => void;
  dataId: string;
  dataType: string;
  dataCategories?: string[];
  onUpdateDefaultRoles?: (defaultRoles: AccessRule[]) => void;
  api: Api;
  dataToVisualize?: SpatialModel[];
}

interface DataSecurityDialogState {
  isLoading: boolean;
  isExecuting: boolean;
  isSuccess: boolean;
  error: Error;
  accessRules: AccessRule[];
  isUserManagementActive: boolean;
  allRoles: Role[];
  dataType: string;
  unmappedRoles: AccessRule[];
  mapDrawModeActive: boolean;
  mapEditModeActive: boolean;
  spatialAreaFeature: Feature;
  prevSpatialAreaFeature: Feature;
  mapFileModeActive: boolean;
  polygonLayer: FeatureLayer;
  polygonLayerReference: string;
  polygonLayerReferenceId: number;
}

const MESSAGES = defineMessages({
  HELP: {
    id: "studio.UI.help-alt",
    defaultMessage: "❔",
  }
});

class DataSecurityDialogComponent extends React.Component<DataSecurityDialogProps & InjectedIntlProps & WithApiProperties, DataSecurityDialogState> {
  private securityForm: any;

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      isExecuting: false,
      isSuccess: false,
      error: null,
      accessRules: [],
      isUserManagementActive: null,
      allRoles: [],
      dataType: this.props.dataType,
      unmappedRoles: [],
      mapDrawModeActive: false,
      mapEditModeActive: false,
      mapFileModeActive: false,
      spatialAreaFeature: null,
      prevSpatialAreaFeature: null,
      polygonLayer: null,
      polygonLayerReference: "epsg:4326",
      polygonLayerReferenceId: 4326,
    };
  }

  componentDidMount() {
    if (this.props.dataType === "none") {
      this.loadDefaults();
    } else {
      this.load();
    }
  }

  private load() {
    const {api, dataId} = this.props;

    this.setState({isLoading: true});
    Promise.all([
      api.getCrawlAccessRules(),
      api.getAccessRules(dataId),
      api.isUserManagementActive(),
      api.getAllRoles(),
    ])
        .then(([
                 unmappedRoles,
                 accessRules,
                 isUserManagementActive,
                 allRoles,
               ]) => {
          this.setState({
            isLoading: false,
            accessRules,
            isUserManagementActive,
            allRoles,
            unmappedRoles,
          });
        });
  }

  private loadDefaults() {
    const {api} = this.props;

    this.setState({isLoading: true});
    Promise.all([
      api.getAllRolesWithDefaultAccessRules(),
      api.getCrawlAccessRules(),
      api.isUserManagementActive(),
      api.getAllRoles(),
    ])
        .then(([
                 unmappedRoles,
                 accessRules,
                 isUserManagementActive,
                 allRoles,
               ]) => {
          this.setState({
            isLoading: false,
            unmappedRoles,
            accessRules,
            isUserManagementActive,
            allRoles,
          });
        });
  }

  closeDialog = () => {
    this.props.onHide();
  }

  setSecurityFormModalRef = ref => {
    if (ref) {
      this.securityForm = ref.getWrappedInstance();
    }
  }

  saveChanges = () => {
    const {dataId, dataType} = this.props;
    this.setState({isLoading: false, isExecuting: true, error: null, isSuccess: false});
    const launchedFromSettingsPage: boolean = dataType === "none";

    const result = this.securityForm.getResult();

    if (launchedFromSettingsPage) {
      if (result.deletedPermissionsToSave?.length > 0) {
        this.props.onUpdateDefaultRoles(result.deletedPermissionsToSave);
      }
      if (result.permissionsToSave?.length > 0) {
        this.props.onUpdateDefaultRoles(result.permissionsToSave);
      }
      this.setState({isExecuting: false, error: null, isSuccess: true});
      this.closeDialog();
      this.props.onSuccess?.();
    } else {
      Promise.resolve()
          .then(() => {
            if (result.deletedPermissionsToSave?.length > 0) {
              return this.props.api.deleteAccessRules(dataId, result.deletedPermissionsToSave);
            }
          })
          .then(() => {
            if (result.permissionsToSave?.length > 0) {
              return this.props.api.setAccessRules(dataId, result.permissionsToSave);
            }
          })
          .then(() => {
            this.setState({isExecuting: false, error: null, isSuccess: true});
            this.closeDialog();
            this.props.onSuccess?.();
          })
          .catch((error) => {
            this.setState({isExecuting: false, error, isSuccess: false});
          });
    }
  }

  updateSpatialAreaFromFile = (data) => {
    if (data.target?.files?.length === 1) {
      const file = data.target.files[0];
      if (file.name.toLowerCase().endsWith(".gml") || file.name.toLowerCase().endsWith(".geojson")) {
        const reader = new FileReader();
        reader.onerror = (e) => {
          this.setState({error: new Error("Error reading file")});
        };
        reader.onload = (e) => {
          const content = e.target.result;
          const blob = new Blob([content], {type: 'text/plain'});
          const url = URL.createObjectURL(blob);
          const wkt = allEpsg[this.state.polygonLayerReferenceId].wkt;
          let reference = parseWellKnownText(wkt);
          if (reference?.identifier) {
            if (!isValidReferenceIdentifier(reference.identifier)) {
              addReference(reference);
            }
          } else {
            reference = null;
          }

          const store = new UrlStore({
            target: url,
            codec: file.name.toLowerCase().endsWith(".geojson") ? new GeoJsonCodec() : new GMLCodec(),
            reference
          });
          const model = new FeatureModel(store, {reference});
          const layer = new FeatureLayer(model, {selectable: true, hoverable: true, label: "polygonLayer"});
          this.setState({polygonLayer: layer, mapFileModeActive: true});
        }
        this.setState({spatialAreaFeature: null, polygonLayer: null, mapFileModeActive: false});
        if (file) {
          reader.readAsText(file);
        }
      }
    }
  }

  private onDrawSpatialArea = () => {
    this.setState(prevState => ({
      mapDrawModeActive: true,
      spatialAreaFeature: null,
      prevSpatialAreaFeature: prevState.spatialAreaFeature
    }));
  }

  private onEditSpatialArea = (spatialAreaFeature: Feature) => {
    this.setState(prevState => ({
      mapEditModeActive: true,
      spatialAreaFeature,
      prevSpatialAreaFeature: prevState.spatialAreaFeature,
    }));
  }

  private onFileSpatialArea = () => {
    this.setState(prevState => ({
      mapFileModeActive: true,
      polygonLayer: null,
      spatialAreaFeature: null,
      prevSpatialAreaFeature: prevState.prevSpatialAreaFeature,
    }));
  }

  private onDrawCancel = () => {
    this.setState(prevState => ({
      mapDrawModeActive: false,
      spatialAreaFeature: prevState.prevSpatialAreaFeature
    }));
  }

  private onEditCancel = () => {
    this.setState(prevState => ({
      mapEditModeActive: false,
      spatialAreaFeature: prevState.prevSpatialAreaFeature
    }));
  }

  private onFileSpatialAreaCancel = () => {
    this.setState(prevState => ({
      polygonLayer: null,
      mapFileModeActive: false,
      spatialAreaFeature: prevState.prevSpatialAreaFeature
    }));
  }

  private onFeatureDrawn = (spatialAreaFeature: Feature) => {
    this.setState({
      spatialAreaFeature,
      mapDrawModeActive: false,
      mapEditModeActive: false,
      mapFileModeActive: false,
      polygonLayer: null,
    });
  }

  private handlePolygonLayerReferenceChanged = (event) => {
    const value = event.target.value;
    const referenceId: number = validEPSGCode(value);
    if (referenceId) {
      this.setState({polygonLayerReference: value, polygonLayerReferenceId: referenceId, error: null});
      return;
    }
    this.setState({
      polygonLayerReference: value,
      polygonLayerReferenceId: null,
      error: new Error("Invalid reference identifier")
    });
  }

  renderTitle() {
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }
    return <Modal.Title><FormattedMessage id="studio.data-security-dialog.title"
                                          defaultMessage="Security"/></Modal.Title>;
  }

  renderButtons() {
    const {isExecuting, isSuccess, mapDrawModeActive, mapEditModeActive, mapFileModeActive} = this.state;

    if (isExecuting || isSuccess || mapDrawModeActive || mapEditModeActive || mapFileModeActive) {
      return null;
    }

    return (
        <div>
          <Button onClick={this.saveChanges}>
            <FormattedMessage id="studio.data-security-dialog.save-button" defaultMessage="Save"/>
          </Button>
          <Button data-cy="cancel" onClick={this.closeDialog}><FormattedMessage
              id="studio.ui.form-buttons.cancel"
              defaultMessage="Cancel"/></Button>
        </div>
    );
  }

  renderMessage(message: React.ReactElement) {
    return (
        <div style={{textAlign: "center"}}>
          <h2>{message}</h2>
          <Spinner bigStyle/>
        </div>
    );
  }

  renderExecuting() {
    return this.renderMessage(<FormattedMessage id="studio.data-security-dialog.submitting"
                                                defaultMessage="Submitting changes..."/>);
  }

  renderLoading() {
    return this.renderMessage(<FormattedMessage id="studio.data-security-dialog.loading" defaultMessage="Loading..."/>);
  }

  renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  renderBody() {
    const {isLoading, isExecuting, dataType} = this.state;
    const {api} = this.props;
    const background = api.getPreviewBackground();

    // If dataType is "none", this was called from Settings-> Crawling Defaults page.
    const addDefaultRoles = dataType === "none";

    if (isLoading) {
      return this.renderLoading();
    }

    if (isExecuting) {
      return this.renderExecuting();
    }

    return (
        <div>
          {this.renderError()}
          <div style={{
            display: (!this.state.mapDrawModeActive && !this.state.mapEditModeActive &&
                      !this.state.mapFileModeActive && !this.state.polygonLayer) ? "block" : "none",
          }}>
            <SecurityForm
                key={`security-form-${isLoading}`}
                ref={this.setSecurityFormModalRef}
                accessRules={this.state.accessRules}
                isUserManagementActive={this.state.isUserManagementActive}
                allRoles={this.state.allRoles}
                dataType={this.props.dataType}
                dataCategories={this.props.dataCategories}
                onDrawSpatialArea={this.onDrawSpatialArea}
                onEditSpatialArea={this.onEditSpatialArea}
                onFileSpatialArea={this.onFileSpatialArea}
                spatialAreaFeature={this.state.spatialAreaFeature}
                addCrawlDefaultRoles={addDefaultRoles}
                unmappedRoles={this.state.unmappedRoles}
            />
          </div>
          {(this.state.mapFileModeActive && !this.state.polygonLayer) &&
           <div>
             <h2>
               <FormattedMessage id="studio.data-security-dialog.file-title" defaultMessage="Select file"/>
             </h2>
             <div>
               <Form horizontal>
                 <FormGroup controlId="CRS" className={"spatial-group"}>
                   <Col sm={2}>
                     <ControlLabel>
                       <FormattedMessage id="studio.data-security-dialog.file-crs" defaultMessage="CRS"/>
                     </ControlLabel>
                   </Col>
                   <Col sm={8}>
                     <InputGroup>
                       <input
                           className="form-control"
                           type="text"
                           value={this.state.polygonLayerReference}
                           onChange={this.handlePolygonLayerReferenceChanged}
                       />
                     </InputGroup>
                   </Col>
                   <Col sm={1} className={"config-help-icon"}>
                     <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip_help">
                       <FormattedMessage id="studio.data-security-dialog.crs.help"
                                         defaultMessage="This is the native EPSG of the provided polygon definitions. The input polygons will be transformed automatically to EPSG:4326 to meet API requirements"/>
                     </Tooltip>}>
                       <img src={`theme/assets/images/help_16.png`}
                            alt={this.props.intl.formatMessage(MESSAGES.HELP)}/>
                     </OverlayTrigger>
                   </Col>
                 </FormGroup>

                 <FormGroup controlId="selectedFile" className={"spatial-group"}>
                   <Col sm={2}>
                     <ControlLabel>
                       <FormattedMessage id="studio.data-security-dialog.file" defaultMessage="File"/>
                     </ControlLabel>
                   </Col>
                   <Col sm={8}>
                     <InputGroup>
                       <FormControl name="file"
                                    type="file"
                                    accept={".gml, .geojson"}
                                    multiple={false}
                                    onChange={this.updateSpatialAreaFromFile}
                                    onSelect={this.updateSpatialAreaFromFile}
                                    disabled={this.state.polygonLayerReferenceId === null}
                       />
                     </InputGroup>
                   </Col>
                   <Col sm={1} className={"config-help-icon"}>
                     <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip_help">
                       <FormattedMessage id="studio.data-security-dialog.file.help"
                                         defaultMessage="GeoJSON or GML v3 Polygon definition file. Multi-part polygon geometry objects are not supported for Spatial Area masking"/>
                     </Tooltip>}>
                       <img src={`theme/assets/images/help_16.png`}
                            alt={this.props.intl.formatMessage(MESSAGES.HELP)}/>
                     </OverlayTrigger>
                   </Col>
                 </FormGroup>
               </Form>
               <div style={{float: "right"}}>
                 <Button onClick={() => {
                   this.setState({polygonLayer: null, mapFileModeActive: false})
                 }}>
                   <FormattedMessage id="studio.data-security-dialog.back-button" defaultMessage="Back"/>
                 </Button>
               </div>
             </div>
           </div>
          }
          {(this.state.mapDrawModeActive || this.state.mapEditModeActive) &&
           <RIAMap style={{width: "100%", height: "400px"}}
                   api={this.props.api}
                   wmsUrl={this.props.dataToVisualize ? this.props.api.getDataPreviewBaseUrl() : null}
                   background={background}
                   dataToVisualize={this.props.dataToVisualize ? this.props.dataToVisualize : []}
                   shouldFit={true}
                   shouldAnimateFit={false}
                   restrictNavigationBounds={background.bounds ? background.bounds : RESTRICT_NAVIGATION_BOUNDS}
                   shouldHideLayerTree={true}
                   shouldAddGridLayer={false}
                   showPreview={true}
                   drawModeActive={this.state.mapDrawModeActive}
                   editModeActive={this.state.mapEditModeActive}
                   onDrawCancel={this.onDrawCancel}
                   onEditCancel={this.onEditCancel}
                   editFeature={this.state.spatialAreaFeature}
                   onFeatureDrawn={this.onFeatureDrawn}
                   enableComplexPolygon={false} //set to true once Multipolygons are supported
           />
          }
          {this.state.polygonLayer &&
           <RIAMap style={{width: "100%", height: "400px"}}
                   api={this.props.api}
                   wmsUrl={this.props.dataToVisualize ? this.props.api.getDataPreviewBaseUrl() : null}
                   background={background}
                   dataToVisualize={this.props.dataToVisualize ? this.props.dataToVisualize : []}
                   shouldFit={true}
                   shouldAnimateFit={false}
                   restrictNavigationBounds={background.bounds ? background.bounds : RESTRICT_NAVIGATION_BOUNDS}
                   shouldHideLayerTree={true}
                   shouldAddGridLayer={false}
                   showPreview={true}
                   onFileSpatialAreaCancel={this.onFileSpatialAreaCancel}
                   polygonLayer={this.state.polygonLayer}
                   polygonLayerReference={this.state.polygonLayerReference}
                   onFeatureDrawn={this.onFeatureDrawn}
           />
          }
        </div>
    );
  }

  render() {

    const {isExecuting} = this.state;
    return (
        <Modal className="data-security-dialog"
               data-cy="data-security-dialog"
               show={true}
               style={{"visibility": "visible"}}
               onHide={this.closeDialog}
               backdrop={"static"}>
          <Modal.Header closeButton={!isExecuting}>
            {this.renderTitle()}
          </Modal.Header>
          <Modal.Body id={"add-modal-body"}>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.renderButtons()}
          </Modal.Footer>
        </Modal>
    );
  }
}

export const DataSecurityDialog = WithApi(injectIntl(DataSecurityDialogComponent));